import { useEffect, useRef, useState } from 'react'
import { loadGoogleMapsPlaces } from '../utils/loadGoogleMapsPlaces'
import { parseGoogleAddressComponents } from '../utils/parseAddressComponents'

/**
 * @typedef {Object} PlaceSelection
 * @property {number} latitude
 * @property {number} longitude
 * @property {string} city
 * @property {string} state
 * @property {string} country
 * @property {string} eventLocation
 * @property {string} formattedAddress
 */

/**
 * Attaches Google Places Autocomplete to inputRef and reports resolved places.
 * @param {React.RefObject<HTMLInputElement|null>} inputRef
 * @param {(selection: PlaceSelection | null) => void} onPlaceSelected
 * @returns {{ mapsLoading: boolean, mapsError: string|null }}
 */
export function usePlaceAutocomplete(inputRef, onPlaceSelected) {
  const [mapsLoading, setMapsLoading] = useState(true)
  const [mapsError, setMapsError] = useState(null)
  const acRef = useRef(null)
  const onPlaceSelectedRef = useRef(onPlaceSelected)
  onPlaceSelectedRef.current = onPlaceSelected

  useEffect(() => {
    let cancelled = false
    const el = inputRef.current

    if (!el) {
      setMapsLoading(false)
      return () => {
        cancelled = true
      }
    }

    loadGoogleMapsPlaces()
      .then(() => {
        if (cancelled || !inputRef.current) return

        setMapsLoading(false)
        setMapsError(null)

        const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
          fields: ['geometry', 'formatted_address', 'address_components', 'name'],
        })
        acRef.current = ac

        ac.addListener('place_changed', () => {
          const place = ac.getPlace()
          const loc = place.geometry?.location
          if (!loc) {
            onPlaceSelectedRef.current?.(null)
            return
          }

          const { city, state, country } = parseGoogleAddressComponents(place.address_components)
          const latitude = typeof loc.lat === 'function' ? loc.lat() : loc.lat
          const longitude = typeof loc.lng === 'function' ? loc.lng() : loc.lng

          const name = place.name?.trim() || ''
          const formatted = place.formatted_address?.trim() || ''

          let eventLocation = formatted
          if (name && formatted && !formatted.toLowerCase().startsWith(name.toLowerCase())) {
            eventLocation = `${name}, ${formatted}`
          } else if (name && !formatted) {
            eventLocation = name
          }

          onPlaceSelectedRef.current?.({
            latitude,
            longitude,
            city,
            state,
            country,
            eventLocation,
            formattedAddress: formatted || name,
          })
        })
      })
      .catch((err) => {
        if (!cancelled) {
          setMapsError(err?.message || 'Could not load address search')
          setMapsLoading(false)
        }
      })

    return () => {
      cancelled = true
      if (acRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(acRef.current)
      }
      acRef.current = null
    }
  }, [inputRef])

  return { mapsLoading, mapsError }
}
